import { useState, useEffect, useLayoutEffect, useRef } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { pathToRegexp } from 'path-to-regexp'

import { checkAndHandleInterrupts, Session } from '@ally/transmitigator'
import { fetchAndSetCif, logRocketTrack } from '@ally/ally-login'

import log from '../whisper'
import { useAppState } from '../AppStateProvider'
import { useFlagContextValue } from '../FlagContext'

interface SessionPayload {
  type: 'Session'
  data: Session
}

interface TicketPayload {
  type: 'Ticket'
  data: string
}

export type AuthPayload = SessionPayload | TicketPayload

interface UseRedirectPathProps {
  defaultAuthRoute: string
  sessionRoutes?: string[] | null
  authPayload: AuthPayload | null
  isEnabled?: boolean
}

export type RedirectPathState =
  | 'UseSession'
  | 'UseTicket'
  | 'RedirectInternal'
  | 'RedirectExternal'

export interface RedirectPath {
  state: RedirectPathState
  route: string
}

interface UseRedirectPathOutput {
  redirectPath: RedirectPath
}

const getRedirectRoute = ({ route }: RedirectPath, ticketID: string): string =>
  route.includes('?')
    ? `${route}&ticketID=${ticketID}`
    : `${route}?ticketID=${ticketID}`

const checkForReactRoute = (routes: string[], path: string): boolean => {
  const updatedPath = path.split('?')[0]
  return routes.some(route => {
    const regEx = pathToRegexp(route)
    return regEx.test(updatedPath)
  })
}

/**
 * If variations are loaded, use ticket if dashboard.
 *
 * @param sessionRoutes
 * @param route
 */
const checkIsDashboardRoute = (
  sessionRoutes: string[] | null,
  route: string,
): RedirectPathState => {
  // We're using the presence of sessionRoutes to determine behavior, we aren't concerned about its contents.
  // This is necessary for local dev, where we wouldn't want a ticketID for dashboard.
  // TODO: remove this weirdness after dashboard is in next.
  if (!sessionRoutes) {
    return 'UseSession'
  }

  return checkForReactRoute(['/dashboard'], route) ? 'UseTicket' : 'UseSession'
}

/**
 * This React hook encapsulates Ally's business logic regarding where to take a user once they become authenticated.
 * It returns an optional `RedirectPath` object that indicates whether or not the redirect location is external or
 * internal. This package also exports an `<AllyRedirect />` component that handles both outcomes.
 *
 * @param defaultAuthRoute
 * @param session
 * @param isEnabled
 */
export const useRedirectPath = ({
  defaultAuthRoute,
  authPayload,
  sessionRoutes = null,
  isEnabled = true,
}: UseRedirectPathProps): UseRedirectPathOutput => {
  const location = useLocation()
  const hasRunAuthAction = useRef<boolean>(false)
  const { isWebWrapped } = useAppState()
  const history = useHistory()
  const { variation } = useFlagContextValue()

  const [redirectPath, setRedirectPath] = useState<RedirectPath>(() => {
    const getRoute = (): string => {
      const optionalRedirectPath = new URLSearchParams(location.search).get(
        'redirect',
      )

      if (optionalRedirectPath) {
        return optionalRedirectPath
      }

      return defaultAuthRoute
    }

    const route = getRoute()

    return {
      route,
      state: checkIsDashboardRoute(sessionRoutes, route),
    }
  })

  useEffect(() => {
    const optionalRedirectPath = new URLSearchParams(location.search).get(
      'redirect',
    )

    if (optionalRedirectPath) {
      // Do not update redirectPath.state when it is already in a "Redirect" state
      const skipFor: RedirectPathState[] = [
        'RedirectInternal',
        'RedirectExternal',
      ]
      setRedirectPath(prevRedirectPath => ({
        ...prevRedirectPath,
        route: optionalRedirectPath,
        ...(!skipFor.includes(prevRedirectPath.state) && {
          state: checkIsDashboardRoute(sessionRoutes, optionalRedirectPath),
        }),
      }))
    }
  }, [location.search])

  useLayoutEffect(() => {
    if (isEnabled && authPayload && !hasRunAuthAction.current) {
      // I wouldn't include this, but I don't want to fire the storefront reviews call more than once
      hasRunAuthAction.current = true
      logRocketTrack('login-transmit-authenticated')
      switch (authPayload.type) {
        case 'Session': {
          const { SSOURL, SSOREQUIRED } = authPayload.data

          if (SSOURL && SSOREQUIRED) {
            log.info({ message: 'Found SSOURL Redirecting...' })

            setRedirectPath({
              state: 'RedirectExternal',
              route: SSOURL as string,
            })

            return
          }

          if (isWebWrapped) {
            setRedirectPath(() => ({
              state: 'RedirectExternal',
              route: 'ally://login',
            }))

            return
          }

          // TODO: this 'STOREFRONT_REVIEWS_PAGE' route is now deprecated, and can be removed after
          // storefront updates their link with '/ext-storefront/bank/customer-reviews/' instead
          if (redirectPath.route === 'STOREFRONT_REVIEWS_PAGE') {
            const onCifFetchComplete = (): void => {
              setRedirectPath({
                state: 'RedirectInternal',
                route: '/ext-storefront/bank/customer-reviews/',
              })
            }

            const onFailure = (): void => {
              setRedirectPath({
                state: 'RedirectInternal',
                route: '/temp-unavailable',
              })
            }

            fetchAndSetCif(authPayload.data, onCifFetchComplete, onFailure)

            return
          }

          if (redirectPath.route.startsWith('/ext-')) {
            // A user's cif will be returned in the authPayload with the AFG Transmit response
            // Until that is ready, we will need to do a fetch for the cif for these specific routes
            if (
              redirectPath.route === '/ext-storefront/bank/customer-reviews/' ||
              redirectPath.route ===
                '/ext-storefront/learn/webroot/download.html'
            ) {
              const onCifFetchComplete = (): void =>
                setRedirectPath({
                  state: 'RedirectInternal',
                  route: redirectPath.route,
                })

              const onFailure = (): void =>
                setRedirectPath({
                  state: 'RedirectInternal',
                  route: '/temp-unavailable',
                })

              fetchAndSetCif(authPayload.data, onCifFetchComplete, onFailure)

              return
            }

            setRedirectPath({
              state: 'RedirectInternal',
              route: redirectPath.route,
            })

            return
          }

          // There is nothing returned to indicate whats happening, however if any navigation
          // is required, it will cause this remote to unload and it won't matter what state
          // this hook is in
          checkAndHandleInterrupts(
            authPayload.data,
            history.push,
            variation,
            redirectPath.route,
          )

          setRedirectPath(prevRedirectPath => ({
            ...prevRedirectPath,
            state: 'RedirectInternal',
          }))

          break
        }

        case 'Ticket': {
          setRedirectPath(prevRedirectPath => ({
            route: getRedirectRoute(prevRedirectPath, authPayload.data),
            state: 'RedirectInternal',
          }))

          break
        }

        default:
          break
      }
    }
  }, [isEnabled, redirectPath, authPayload])

  return {
    redirectPath,
  }
}
